import { useAtomValue } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { ChatMessageType } from '@winging/util/type/chat.type';
import {
  conversationsSelector,
  isChatMessageBusySelector,
  isPollingSelector,
  lastChatMessageSelector,
} from '@/entities/chat';
import DemoRequestItem from '@/features/demo/ui/DemoRequestItem';
import { usePathname } from '@/i18n/navigation';
import DemoChatQuestion from '@/features/demo/ui/DemoChatQuestion';
import DemoChatAnswer from '@/features/demo/ui/DemoChatAnswer';
import { useWindowWidth } from '@/shared/lib/hooks/useWindowSize';
import { useDebouncedCallback } from '@/shared/lib/hooks/useDebouncedCallback';

const DemoChat = () => {
  const conversations = useAtomValue(conversationsSelector);
  const lastChatMessage = useAtomValue(lastChatMessageSelector);
  const isPolling = useAtomValue(isPollingSelector);
  const isBusy = useAtomValue(isChatMessageBusySelector);
  const pathname = usePathname();
  const width = useWindowWidth();

  const [shouldAnimate, setShouldAnimate] = useState(false);
  const lastConversationRef = useRef<HTMLDivElement | null>(null);
  const lastAnswerRef = useRef<HTMLDivElement | null>(null);

  const debouncedScrollIntoView = useDebouncedCallback(
    (node: HTMLElement) =>
      node.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    200
  );

  const setLastAnswerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        return;
      }

      lastAnswerRef.current = node;
      debouncedScrollIntoView(node);
    },
    [debouncedScrollIntoView]
  );

  useEffect(() => {
    if (!lastConversationRef.current || !width || width > 1280) {
      return;
    }

    const timeoutId = setTimeout(() => {
      lastConversationRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [conversations.length, width]);

  useEffect(() => {
    if (lastChatMessage?.type === ChatMessageType.InsertData && !isBusy) {
      setShouldAnimate(true);
    }
  }, [isBusy, lastChatMessage]);

  return (
    <div
      className={`text-default-black w-full ${
        !conversations.length
          ? 'mb-0'
          : pathname === '/'
            ? 'mb-[50px]'
            : 'main:mb-[197px] mb-[140px]'
      } text-body2 main:gap-10 flex flex-col flex-wrap gap-[24px]`}
    >
      {conversations.map((conversation, index) => {
        const lastConversationMessage = conversation.messages.at(-1);

        const showChatFooter =
          lastConversationMessage &&
          (!isPolling || lastChatMessage?._id !== lastConversationMessage._id);
        return (
          <div
            ref={
              index === conversations.length - 1 ? lastConversationRef : null
            }
            key={index}
            className="conversation main:gap-[28px] flex w-full flex-col gap-[24px]"
          >
            {conversation?.question?.length > 0 && (
              <DemoChatQuestion question={conversation.question} />
            )}
            {conversation?.answer?.length > 0 && (
              <DemoChatAnswer
                answer={conversation.answer}
                answerRef={
                  index === conversations.length - 1 ? setLastAnswerRef : null
                }
              />
            )}

            {showChatFooter &&
              lastConversationMessage!.type === ChatMessageType.InsertData && (
                <DemoRequestItem shouldAnimate={shouldAnimate} />
              )}
          </div>
        );
      })}
    </div>
  );
};
export default DemoChat;
